import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import dayjs from 'dayjs';
import api from '../services/api';
import { VoucherForm } from '../components/voucher/VoucherForm';
import type {
  DiscountType,
  InitialStatus,
} from '../components/voucher/VoucherForm';

export default function CreateVoucher() {
  const navigate = useNavigate();

  const handleSubmit = async (values: Record<string, unknown>) => {
    const code = String(values.code || '')
      .trim()
      .toUpperCase();
    const name = String(values.campaignName || '').trim();
    const description = String(values.description || '').trim();
    const discountTypeVal = values.discountType as DiscountType;
    const discountValue = Number(values.discountValue);
    let minimumOrderAmount = Number(values.minimumSpend);
    let maxDiscountAmount: number | undefined =
      values.maxDiscountAmount != null && values.maxDiscountAmount !== ''
        ? Number(values.maxDiscountAmount)
        : undefined;

    if (!code || !name) {
      message.error('Campaign Name and Voucher Code are required.');
      return;
    }
    if (isNaN(discountValue) || discountValue <= 0) {
      message.error('Discount Value must be greater than 0.');
      return;
    }
    if (
      discountTypeVal === 'percentage' &&
      (discountValue > 100 || discountValue < 0)
    ) {
      message.error('Percentage must be between 0 and 100.');
      return;
    }
    if (isNaN(minimumOrderAmount)) minimumOrderAmount = 0;
    if (minimumOrderAmount < 0) minimumOrderAmount = 0;
    if (
      maxDiscountAmount != null &&
      (isNaN(maxDiscountAmount) || maxDiscountAmount < 0)
    ) {
      maxDiscountAmount = undefined;
    }

    const initialStatus = values.initialStatus as InitialStatus;
    const now = new Date();
    let validFrom: string;
    let validUntil: string;
    if (initialStatus === 'active_immediately') {
      validFrom = now.toISOString();
      validUntil = new Date(
        now.getTime() + 365 * 24 * 60 * 60 * 1000
      ).toISOString();
    } else {
      const fromVal = values.validFrom;
      const untilVal = values.validUntil;
      validFrom = fromVal
        ? dayjs(fromVal as string).toISOString()
        : now.toISOString();
      validUntil = untilVal
        ? dayjs(untilVal as string).toISOString()
        : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
    }

    const body: Record<string, unknown> = {
      code,
      name,
      discount_type: discountTypeVal,
      discount_value: discountValue,
      minimum_order_amount: minimumOrderAmount,
      valid_from: validFrom,
      valid_until: validUntil,
      isActive: true,
      isPublic: true,
    };
    if (description) body.description = description;
    if (
      discountTypeVal === 'percentage' &&
      maxDiscountAmount != null &&
      maxDiscountAmount > 0
    ) {
      body.max_discount_amount = maxDiscountAmount;
    }

    try {
      await api.post('/discount-codes/create', body);
      message.success('Voucher created successfully');
      navigate('/vouchers');
    } catch (err: unknown) {
      const ax = err as {
        response?: {
          data?: {
            message?: string;
            errors?: Array<{ msg?: string; path?: string }>;
          };
        };
      };
      const data = ax.response?.data;
      const errors = data?.errors;
      const msg =
        Array.isArray(errors) && errors.length > 0
          ? errors
              .map(e => e.msg)
              .filter(Boolean)
              .join('; ') ||
            data?.message ||
            'Failed to create voucher'
          : data?.message || 'Failed to create voucher';
      message.error(msg);
      throw err;
    }
  };

  return (
    <div className="create-voucher-wrap">
      <div className="create-voucher-breadcrumb">
        <Link to="/">Dashboard</Link>
        <span className="create-voucher-breadcrumb-sep">&gt;</span>
        <Link to="/vouchers">Vouchers</Link>
        <span className="create-voucher-breadcrumb-sep">&gt;</span>
        <span className="create-voucher-breadcrumb-current">Create New</span>
      </div>

      <h1 className="create-voucher-title">Create New Voucher</h1>

      <div className="create-voucher-card">
        <VoucherForm
          mode="create"
          onSubmit={handleSubmit}
          onCancel={() => navigate('/vouchers')}
          submitLabel="Create Voucher"
        />
      </div>
    </div>
  );
}
